import React, { createContext, useContext, useEffect } from 'react';
import { AlertModalManager } from '../components/AlertModalManager';
import { useAlertModal } from '../hooks/useAlertModal';
import { Alert, AlertModalContextType, AlertModalProviderProps, UserInfo } from '../types';

const AlertModalContext = createContext<AlertModalContextType | undefined>(undefined);

export const useAlertModalContext = () => {
  const context = useContext(AlertModalContext);
  if (!context) {
    throw new Error('useAlertModalContext must be used within AlertModalProvider');
  }
  return context;
};

export const AlertModalProvider: React.FC<AlertModalProviderProps> = ({ children }) => {
  const { isOpen, modalContent, openModal, closeModal } = useAlertModal();

  useEffect(() => {
    const handleOpenModal = (event: CustomEvent) => {
      openModal(event.detail);
    };

    window.addEventListener('openAlertModal', handleOpenModal as EventListener);

    return () => {
      window.removeEventListener('openAlertModal', handleOpenModal as EventListener);
    };
  }, [openModal]);

  return (
    <AlertModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <AlertModalManager modalContent={modalContent} isOpen={isOpen} onClose={closeModal} />
    </AlertModalContext.Provider>
  );
};

export class AlertSystem {
  public alerts: Alert[];
  public userInfo: UserInfo | null;
  private dismissedAlerts: string[];

  constructor(alerts: Alert[], userInfo: UserInfo | null = null) {
    this.alerts = alerts;
    this.userInfo = userInfo;
    this.dismissedAlerts = this.loadDismissedAlerts();
  }

  private loadDismissedAlerts(): string[] {
    const dismissed = localStorage.getItem('dismissedAlerts');
    return dismissed ? JSON.parse(dismissed) : [];
  }

  private saveDismissedAlerts(): void {
    localStorage.setItem('dismissedAlerts', JSON.stringify(this.dismissedAlerts));
  }

  public dismissAlert(alertId: string): void {
    if (!this.dismissedAlerts.includes(alertId)) {
      this.dismissedAlerts.push(alertId);
      this.saveDismissedAlerts();
    }
  }

  public shouldShowAlert(alert: Alert): boolean {
    if (this.dismissedAlerts.includes(alert.id)) {
      return false;
    }

    if (alert.target === 'all') {
      if (alert.excludeRoles && this.userInfo?.isLogged) {
        return !alert.excludeRoles.includes(this.userInfo.role || '');
      }
      return true;
    }

    if (alert.target === 'logged') {
      if (!this.userInfo?.isLogged) return false;
      if (alert.excludeRoles) {
        return !alert.excludeRoles.includes(this.userInfo.role || '');
      }
      return true;
    }

    if (alert.target === 'not_logged') {
      return !this.userInfo?.isLogged;
    }

    if (Array.isArray(alert.target)) {
      return this.userInfo?.isLogged === true && alert.target.includes(this.userInfo.role || '');
    }

    return false;
  }

  public getAlertsToShow(): Alert[] {
    return this.alerts.filter(alert => this.shouldShowAlert(alert)).sort((a, b) => a.priority - b.priority);
  }
}
